/**
 * Control Character Sanitization Utilities
 *
 * Control character injection is a security vulnerability where attackers can use Unicode
 * control characters to disguise malicious content by manipulating text display order,
 * direction, or other rendering properties. This can be used to trick users into
 * clicking on malicious links or executing unintended actions.
 *
 * This module provides utilities to detect and sanitize dangerous control characters in user input.
 */

// Dangerous Unicode control characters
const RTLO_CHAR = '\u202E'; // Right-to-Left Override
const LRO_CHAR = '\u202D'; // Left-to-Right Override
const RLE_CHAR = '\u202B'; // Right-to-Left Embedding
const LRE_CHAR = '\u202A'; // Left-to-Right Embedding
const PDF_CHAR = '\u202C'; // Pop Directional Formatting
const LTR_CHAR = '\u200E'; // Left-to-Right Mark
const RTL_CHAR = '\u200F'; // Right-to-Left Mark

// Control character definitions using Map
export const CONTROL_CHAR_MAP = new Map([
  [RTLO_CHAR, '[RTLO]'],
  [LRO_CHAR, '[LRO]'],
  [RLE_CHAR, '[RLE]'],
  [LRE_CHAR, '[LRE]'],
  [PDF_CHAR, '[PDF]'],
  [LTR_CHAR, '[LTR]'],
  [RTL_CHAR, '[RTL]'],
]);

// All dangerous control characters
const CONTROL_CHARS = Array.from(CONTROL_CHAR_MAP.keys());

// Regex pattern to match all control characters
const CONTROL_CHAR_PATTERN = new RegExp(`${CONTROL_CHARS.join('|')}`, 'g');

/**
 * Sanitizes a string by replacing control characters with safe alternatives.
 * For non-string inputs, returns the original input unchanged.
 */
export function sanitizeControlCharacters<T extends string | null | undefined>(
  text: T,
): T {
  if (typeof text === 'string') {
    return text.replace(CONTROL_CHAR_PATTERN, match => {
      return CONTROL_CHAR_MAP.get(match) || '[UNKNOWN]';
    }) as T;
  }

  return text;
}
